import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useMovieContext } from "../context/MovieContext.jsx";
import { useLibraryContext } from "../context/LibraryContext.jsx";
import { useUIContext } from "../context/UIContext.jsx";
import MovieCard from "../components/MovieCard.jsx";
import LoadingSpinner from "../components/LoadingSpinner.jsx";
import { fetchActorById, fetchActorMovies } from "../api/tmdbApi.js";

const ActorDetailPage = ({
  handleBackToHome,
  handleMovieClick,
}) => {
  const {
    selectedActor,
    actorBiography,
    actorMovies,
    isLoadingActorDetails,
    isLoadingActorMovies,
    actorDetailsError,
    actorMoviesCurrentPage,
    hasMoreActorMovies,
    handleLoadMoreActorMovies,
    genresMap,
  } = useMovieContext();

  const { userLibrary, addMovieToLibrary, removeMovieFromLibrary } = useLibraryContext();
  const { showSnackbar } = useUIContext();
  const { id: actorIdParam } = useParams();
  const [localActor, setLocalActor] = useState(null);
  const [localBiography, setLocalBiography] = useState("");
  const [localMovies, setLocalMovies] = useState([]);
  const [isLoadingLocalActor, setIsLoadingLocalActor] = useState(false);
  const [isLoadingLocalMovies, setIsLoadingLocalMovies] = useState(false);
  const [localError, setLocalError] = useState("");
  const [localCurrentPage, setLocalCurrentPage] = useState(1);
  const [localHasMore, setLocalHasMore] = useState(true);

  // Fetch actor details and filmography if not present or mismatched
  useEffect(() => {
    const actorId = actorIdParam || (selectedActor && selectedActor.id);
    if (!actorId) return;

    if (!selectedActor || selectedActor.id !== actorId) {
      // Fetch actor details
      const loadActor = async () => {
        setIsLoadingLocalActor(true);
        setLocalError("");
        const { data, error } = await fetchActorById(actorId);

        if (error) {
          setLocalError(error);
          showSnackbar(error, 'error');
        } else {
          setLocalActor(data);
          setLocalBiography(data?.biography || "");
        }
        setIsLoadingLocalActor(false);
      };

      // Fetch actor movies
      const loadActorMovies = async () => {
        setIsLoadingLocalMovies(true);
        const { data, error } = await fetchActorMovies({
          actorId,
          page: 1,
          genresMap,
        });

        if (error) {
          setLocalError(error);
          showSnackbar(error, 'error');
        } else {
          setLocalMovies(data.movies);
          setLocalCurrentPage(1);
          setLocalHasMore(data.hasMore);
        }
        setIsLoadingLocalMovies(false);
      };

      loadActor();
      loadActorMovies();
    } else {
      setLocalActor(selectedActor);
      setLocalBiography(actorBiography);
      setLocalMovies(actorMovies);
      setLocalCurrentPage(actorMoviesCurrentPage);
      setLocalHasMore(hasMoreActorMovies);
    }
  }, [actorIdParam, selectedActor, genresMap, actorBiography, actorMovies, actorMoviesCurrentPage, hasMoreActorMovies, showSnackbar]);

  const handleLoadMore = async () => {
    if (!localActor) return;

    setIsLoadingLocalMovies(true);
    const { data, error } = await fetchActorMovies({
      actorId: localActor.id,
      page: localCurrentPage + 1,
      genresMap,
    });

    if (error) {
      setLocalError(error);
      showSnackbar(error, 'error');
    } else {
      // Append to existing movies
      setLocalMovies(prev => [...prev, ...data.movies]);
      setLocalCurrentPage(localCurrentPage + 1);
      setLocalHasMore(data.hasMore);
    }
    setIsLoadingLocalMovies(false);
  };

  if (isLoadingLocalActor || !localActor) {
    return <LoadingSpinner message="Loading actor details..." />;
  }

  return (
    <section className="mb-12">
      <button
        onClick={handleBackToHome}
        className="mb-8 bg-[#141414] hover:bg-[#333] text-white font-bold py-2 px-6 transition duration-300 ease-in-out flex items-center shadow-md"
      >
        <svg
          className="w-5 h-5 mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M10 19l-7-7m0 0l7-7m-7 7h18"
          ></path>
        </svg>
        Back to Home
      </button>

      {isLoadingLocalActor && localBiography === "" ? (
        <LoadingSpinner message="Loading actor details..." />
      ) : localError ? (
        <p className="text-center text-red-500 text-lg mt-8">{localError}</p>
      ) : (
        <div className="bg-gray-900 rounded-lg shadow-2xl p-6 sm:p-8 mb-8 flex flex-col md:flex-row items-center md:items-start space-y-6 md:space-y-0 md:space-x-8">
          <img
            src={localActor.profile_path}
            alt={localActor.name}
            className="w-48 h-auto object-cover rounded-lg shadow-lg border-4 border-gray-700 flex-shrink-0"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = `https://placehold.co/500x750/374151/D1D5DB?text=No+Photo`;
            }}
          />
          <div className="text-center md:text-left flex-grow">
            <h2 className="text-4xl sm:text-5xl font-extrabold mb-3 text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-blue-500 leading-tight">
              {localActor.name}
            </h2>
            <p className="text-gray-200 text-lg leading-relaxed mb-6">
              {localBiography}
            </p>
          </div>
        </div>
      )}

      <h3 className="text-3xl font-extrabold text-white mb-6">Filmography</h3>
      {isLoadingLocalMovies && localMovies.length === 0 ? (
        <LoadingSpinner message="Loading filmography..." />
      ) : localError ? (
        <p className="text-center text-red-500 text-lg mt-8">
          Could not load filmography.
        </p>
      ) : localMovies.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {localMovies.map((movie) => (
            <MovieCard
              key={`actor-${localActor.id}-movie-${movie.id}`}
              movie={movie}
              onCardClick={handleMovieClick}
              onAddToLibrary={addMovieToLibrary}
              onRemoveFromLibrary={removeMovieFromLibrary}
              userLibrary={userLibrary}
            />
          ))}
        </div>
      ) : (
        <p className="text-center text-gray-400 text-lg mt-8">
          No filmography available for this actor.
        </p>
      )}

      {/* Load More Actor Movies Button */}
      {!isLoadingLocalMovies && localHasMore && localMovies.length > 0 && (
        <div className="flex justify-center mt-8">
          <button
            onClick={handleLoadMore}
            className="bg-[#e50914] hover:bg-[#b0060f] text-white font-bold py-3 px-8 transition duration-300 ease-in-out transform hover:scale-105 shadow-lg"
          >
            Load More Filmography
          </button>
        </div>
      )}
      {isLoadingLocalMovies && localMovies.length > 0 && (
        <div className="flex justify-center mt-8">
          <LoadingSpinner size="h-8 w-8" />
        </div>
      )}
    </section>
  );
};

export default ActorDetailPage;
